import type {
  BillingClient,
  OpenCheckoutInput,
  OpenCheckoutResult,
} from './IBillingClient'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

// `@paddle/paddle-js` is dynamically imported inside the factory so the
// Stripe path doesn't pay the bundle cost.

interface PaddleEventCallbackData {
  name?: string
  data?: unknown
}

interface PaddleSdk {
  Checkout: {
    open: (opts: {
      transactionId: string
      settings?: { successUrl?: string }
    }) => void
  }
}

let paddlePromise: Promise<PaddleSdk> | null = null
let pendingResolve: ((result: OpenCheckoutResult) => void) | null = null

async function loadPaddle(
  publicKey: string,
  environment: 'sandbox' | 'production',
): Promise<PaddleSdk> {
  if (!paddlePromise) {
    paddlePromise = (async () => {
      const { initializePaddle } = await import('@paddle/paddle-js')
      const sdk = (await initializePaddle({
        environment,
        token: publicKey,
        eventCallback: (event: PaddleEventCallbackData) => {
          // Paddle.js fires checkout.completed once the buyer's transaction
          // settles, and checkout.closed when the overlay is dismissed
          // without paying. Resolve the openCheckout promise with the
          // matching status so the caller can transition the UI.
          if (!pendingResolve) return
          if (event?.name === 'checkout.completed') {
            const resolve = pendingResolve
            pendingResolve = null
            resolve({ status: 'completed' })
          } else if (event?.name === 'checkout.closed') {
            const resolve = pendingResolve
            pendingResolve = null
            resolve({ status: 'closed' })
          }
        },
      })) as PaddleSdk | undefined
      if (!sdk) {
        throw new Error('initializePaddle returned undefined')
      }
      return sdk
    })()
  }
  return paddlePromise
}

export function createPaddleBillingClient(
  publicKey: string,
  environment: 'sandbox' | 'production',
): BillingClient {
  return {
    async openCheckout(input: OpenCheckoutInput): Promise<OpenCheckoutResult> {
      if (!input.transactionId) {
        throw new Error('Paddle checkout requires a transactionId')
      }

      let paddle: PaddleSdk
      try {
        paddle = await loadPaddle(publicKey, environment)
      } catch (error) {
        logger.logError(error as Error, LogCategory.SUBSCRIPTION, {
          message: 'Failed to load Paddle.js',
        })
        throw error
      }

      // The eventCallback wired in loadPaddle() resolves this promise on
      // checkout.completed / checkout.closed.
      const result = await new Promise<OpenCheckoutResult>((resolve) => {
        pendingResolve = resolve
        paddle.Checkout.open({
          transactionId: input.transactionId!,
          settings: input.successUrl
            ? { successUrl: input.successUrl }
            : undefined,
        })
      })
      return result
    },

    openPortal(url: string): void {
      // Paddle's portal links are full URLs; open in the same tab to mirror
      // Stripe's behavior.
      window.location.href = url
    },
  }
}
